import { query } from '@warp-drive/utilities/json-api';
import { SUBMITTED_ACTIVE_INSTANCE_STATES } from 'tio-data/data/resources/tas-program-instance.ext';
import type { Store } from '@warp-drive/core';
import type { TasGroup as TasGroupModel } from 'tio-data/data/resources/tas-group.type';
import type { TasGrouping as TasGroupingModel } from 'tio-data/data/resources/tas-grouping.type';
import type { TasProgramInstance } from 'tio-data/data/resources/tas-program-instance.type';

/**
 * The program template a grouping attaches, as a string id comparable to a
 * resource id. Reads `memberId` because the API never populates the grouping's
 * `tasProgramTemplate` linkage, stringified because `memberId` may arrive as
 * a JSON number.
 *
 * @returns null when the grouping has no member
 */
export function groupingTemplateId(grouping: TasGroupingModel): string | null {
  const { memberId } = grouping;
  return memberId === null || memberId === undefined ? null : String(memberId);
}

/**
 * Template ids that share a MUTUALLY_EXCLUSIVE group with `programTemplateId`,
 * deduped across groups. Never contains `programTemplateId` itself; empty when
 * no group names the template. Groupings without a memberId are skipped rather
 * than treated as matches.
 */
export function exclusiveSiblingTemplateIds(
  tasGroups: TasGroupModel[],
  programTemplateId: string
): string[] {
  const siblings = new Set<string>();
  for (const group of tasGroups) {
    const memberIds = (group.tasGroupings ?? [])
      .map(groupingTemplateId)
      .filter((id): id is string => !!id);
    if (!memberIds.includes(programTemplateId)) {
      continue;
    }
    for (const id of memberIds) {
      if (id !== programTemplateId) {
        siblings.add(id);
      }
    }
  }
  return [...siblings];
}

/**
 * Asks the API whether the employee holds an in-progress instance of any
 * template that is mutually exclusive with `programTemplateId`. Issues at most
 * one network request — none when no exclusive group names the template, or
 * when the employee or template id is unknown (both resolve false).
 *
 * Deliberately queries with server-side template/state filters and NO
 * `include`: including `tas-program-template` on instance lists makes the
 * serializer emit page-scoped inverse linkage on the included templates, which
 * severs `tasProgramTemplate` links in the store for every instance outside
 * the returned page. The filters also make the answer sound at any account
 * size — any returned row restricts, so pagination cannot hide a match.
 */
export async function loadIsRestrictedByProgramExclusivity(
  store: Store,
  employeeId: string | null | undefined,
  tasGroups: TasGroupModel[],
  programTemplateId: string | null | undefined
): Promise<boolean> {
  if (!employeeId || !programTemplateId) {
    return false;
  }
  const siblingIds = exclusiveSiblingTemplateIds(tasGroups, programTemplateId);
  if (siblingIds.length === 0) {
    return false;
  }

  // Existence is the whole answer, so one row is enough.
  const { content } = (await store.request(
    query<TasProgramInstance>('tas-program-instance', {
      'filter[employee]': employeeId,
      'filter[active]': true,
      'filter[tas-program-template]': siblingIds.join(','),
      'filter[state]': SUBMITTED_ACTIVE_INSTANCE_STATES.join(','),
      'page[limit]': 1,
    })
  )) as { content: { data: TasProgramInstance[] } };

  return content.data.length > 0;
}

export default null; // silence a false warning
